import {
  SHAutocomplete,
  SHFilterOption,
  SHStack,
  SHTextField,
  SHTypography,
} from "@components/design-systems";
import { MINIMUM_DELAY_TIME } from "@constants";
import {
  AnalysisSummaryDTO,
  ProductRecommendationDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { ProductRecommendationTypes } from "@models/platform-analysis/enums/summary/recommendationType";
import { Collapse, useTheme } from "@mui/material";
import { setSingleProductRecommendation } from "@redux/slices/platform-analysis";
import { useAppDispatch } from "@redux/store";
import { debounce } from "lodash";
import { memo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useMount, useToggle } from "react-use";
import { AnalysisSummaryStaticValueConfigs } from "../../../configs";

interface RecommendationSelectProps {
  itemIndex: number;
  selectOptions: SHFilterOption[];
  recommendationData: Partial<ProductRecommendationDTO>;
  maxLength?: number;
  disabled?: boolean;
}

export const RecommendationSelect = memo(
  ({
    itemIndex,
    disabled,
    selectOptions,
    recommendationData,
    maxLength = AnalysisSummaryStaticValueConfigs.MaximumCustomRecommendationLabelCharacters,
  }: RecommendationSelectProps) => {
    const { palette } = useTheme();
    const dispatch = useAppDispatch();

    const { control, getValues } = useFormContext<AnalysisSummaryDTO>();

    const [selectedOption, setSelectedOption] = useState<SHFilterOption | null>(
      null,
    );
    const [additionalCustomLabel, setAdditionalCustomLabel] =
      useState<string>("");
    const [isCustomType, toggleIsCustomType] = useToggle(false);

    const debounceUpdateStoredRecommendation = debounce(
      (
        selectedOption: SHFilterOption | null,
        additionalCustomLabel: string,
      ) => {
        if (!recommendationData.productId) return;

        const newData: Partial<ProductRecommendationDTO> = {
          ...recommendationData,
          recommendationType: selectedOption?.value || null,
          additionalCustomLabel: additionalCustomLabel || "",
        };

        dispatch(
          setSingleProductRecommendation({
            productId: recommendationData.productId,
            recommendation: newData,
          }),
        );
      },
      MINIMUM_DELAY_TIME,
    );

    // Test
    const handleOnChangeType = (type: ProductRecommendationTypes | null) => {
      const opt = selectOptions.find((o) => o.value === type);

      setSelectedOption(opt || null);

      const changedToCustomType = type === ProductRecommendationTypes.Custom;

      if (changedToCustomType !== isCustomType) {
        toggleIsCustomType(changedToCustomType);
      }

      if (!changedToCustomType) {
        setTimeout(() => setAdditionalCustomLabel(""), 300);
      }

      debounceUpdateStoredRecommendation(
        opt || null,
        changedToCustomType ? additionalCustomLabel : "",
      );
    };

    const handleOnChangeAdditionalLabel = (label: string) => {
      setAdditionalCustomLabel(label);
      debounceUpdateStoredRecommendation(selectedOption, label);
    };
    // Init selected value from form

    useMount(() => {
      const formSelectedValue =
        getValues(`productRecommendations.${itemIndex}.recommendationType`) ||
        null;
      const opt = selectOptions.find((o) => o.value === formSelectedValue);
      setSelectedOption(opt || null);
      toggleIsCustomType(
        formSelectedValue === ProductRecommendationTypes.Custom,
      );

      const formAdditionalCustomLabel =
        getValues(
          `productRecommendations.${itemIndex}.additionalCustomLabel`,
        ) || "";
      setAdditionalCustomLabel(formAdditionalCustomLabel);
    });

    return (
      <>
        <SHStack
          direction={"row"}
          height={"100%"}
          flexGrow={1}
          gap={2}
          paddingRight={2}
        >
          <SHStack width={"40%"}>
            <Controller
              control={control}
              name={`productRecommendations.${itemIndex}.recommendationType`}
              render={({ field: { onChange } }) => (
                <SHAutocomplete
                  disabled={disabled}
                  options={selectOptions}
                  value={selectedOption}
                  onChange={(_, opt) => {
                    const value = opt?.value;
                    onChange(value);
                    handleOnChangeType(value);
                  }}
                  textFieldProps={{
                    placeholder: "Select recommendation type",
                  }}
                />
              )}
            />
          </SHStack>

          <SHStack width={"60%"}>
            <Collapse
              in={isCustomType}
              orientation="vertical"
              sx={{
                width: "100%",
                "& .MuiCollapse-wrapperInner": {
                  width: "100%",
                },
              }}
            >
              <SHStack width={"100%"}>
                <Controller
                  control={control}
                  name={`productRecommendations.${itemIndex}.additionalCustomLabel`}
                  render={({ field: { onChange } }) => (
                    <SHTextField
                      fullWidth
                      disabled={disabled}
                      maxLength={maxLength}
                      placeholder="Enter custom label"
                      value={additionalCustomLabel}
                      onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                        handleOnChangeAdditionalLabel(value);
                      }}
                      InputProps={{
                        renderSuffix: () => (
                          <SHTypography
                            variant="caption"
                            color={palette.text.disabled}
                            sx={{ paddingTop: 0.5 }}
                          >
                            {additionalCustomLabel.length}/{maxLength}
                          </SHTypography>
                        ),
                      }}
                    />
                  )}
                />
              </SHStack>
            </Collapse>
          </SHStack>
        </SHStack>
      </>
    );
  },
);
